import React from 'react'
import { Chip, Icon, UtilityCard } from '@adc/ui-core'
import type { IconSize } from '@adc/ui-core'
import type { BuyingCriteriaState } from '@adc/blueprint'
import { toConcreteValue } from '@adc/blueprint'
import classnames from 'classnames/bind'
import styles from './styles.scss'

const cx = classnames.bind(styles)

export interface BuyingCriteriaSummaryCardProps {
  /**
   * The user's saved buying preferences (read-only) — resolved by the app and
   * passed down. Single source of truth lives in the app; this card just renders.
   */
  preferences: BuyingCriteriaState | null
  /** Card heading (differs per surface — e.g. homepage vs PRP). */
  title: string
  /** Supporting copy under the heading. */
  subtitle: string
  /** Called when the user wants to edit criteria (opens the modal/flow). */
  onEditCriteria: () => void
  /**
   * Chrome around the card. `'card'` (default) is the white, bordered,
   * rounded look. `'flat'` drops the border and radius so the card blends
   * into a full-width header band (the PRP look) — width and placement
   * remain the consumer's layout responsibility.
   */
  variant?: 'card' | 'flat'
  /**
   * Optional DS icon name shown in the leading light-info circle (e.g. PRP
   * uses location-crosshairs; the homepage card omits it). No circle when
   * omitted.
   */
  iconName?: string
  /** Size of the leading icon. Defaults to 'large'. */
  iconSize?: IconSize
  /** CTA copy. Defaults to 'Update Buying Criteria'. */
  ctaLabel?: string
  /** Automation ID prefix. */
  elmId?: string
}

const formatPrice = (value: number): string => {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`
  if (value >= 1000) return `$${Math.round(value / 1000)}k`
  return `$${value}`
}

/** Format an enum value (e.g. "VACANT", "NOT_SURE") into a readable label */
const formatEnumLabel = (value: string): string =>
  value.charAt(0) + value.slice(1).toLowerCase().replace(/_/g, ' ')

/**
 * BuyingCriteriaSummaryCard — read-only summary of the user's buying criteria
 * with a CTA to edit. Derives its chips from the preferences in the root user
 * store, so it stays fresh as criteria change. Copy is parameterized
 * (title/subtitle) so each surface (homepage, PRP) can label it.
 *
 * Utility-card anatomy (Figma "utility card call-outs"): leading icon in a
 * light circle, title + subtitle, chips row below, right-aligned CTA.
 *
 * Each data point (location, price, occupancy) renders independently
 * (confirmed by design — Sascha, Slack 2026-09): a read-only chip when the
 * user has set it, or a dashed "+ Add …" chip when they haven't. A partially
 * filled state mixes both (e.g. location chip + dashed price/occupancy).
 */
const BuyingCriteriaSummaryCard: React.FC<BuyingCriteriaSummaryCardProps> = ({
  preferences,
  title,
  subtitle,
  onEditCriteria,
  variant = 'card',
  iconName,
  iconSize = 'large',
  ctaLabel = 'Update Buying Criteria',
  elmId = 'buying-criteria-summary',
}) => {
  // Derive one value per data point (show only the first selection for each).
  // An unset data point renders as a dashed "+ Add …" chip instead.

  // Location — label comes from the location search result title
  // (stored as `label` on BuyingCriteriaLocation)
  const locationChip = (preferences?.locations ?? [])[0]?.label

  // Price — toConcreteValue narrows away intent values (explicit
  // "No Min" / "No Max"), which mean no constraint and produce no chip
  let priceChip: string | undefined
  const priceMin = toConcreteValue(preferences?.price?.min)
  const priceMax = toConcreteValue(preferences?.price?.max)
  if (priceMin && priceMax) {
    priceChip = `${formatPrice(priceMin)} - ${formatPrice(priceMax)}`
  } else if (priceMin) {
    priceChip = `${formatPrice(priceMin)}+`
  } else if (priceMax) {
    priceChip = `Up to ${formatPrice(priceMax)}`
  }

  // Occupancy — use definition label map for value→label ("not_sure" means
  // no constraint and renders as unset)
  const occupancy = preferences?.occupancy ?? []
  const occupancyChip =
    occupancy.length && !occupancy.includes('not_sure')
      ? formatEnumLabel(occupancy[0])
      : undefined

  const criteriaGroups = [
    {
      key: 'location',
      chip: locationChip,
      addLabel: '+ Add location',
      addIconName: 'location-dot',
    },
    {
      key: 'price',
      chip: priceChip,
      addLabel: '+ Add price range',
      addIconName: 'dollar-sign',
    },
    {
      key: 'occupancy',
      chip: occupancyChip,
      addLabel: '+ Add occupancy',
      addIconName: 'house',
    },
  ]

  return (
    <UtilityCard
      variant={variant}
      iconName={iconName}
      iconSize={iconSize}
      title={title}
      subtitle={subtitle}
      elmId={elmId}
      cardElmId={`${elmId}-card`}
      cta={{ label: ctaLabel, onClick: onEditCriteria }}
    >
      <div className={cx('chips')}>
        {criteriaGroups.map(group =>
          group.chip ? (
            <Chip
              key={group.key}
              content={group.chip}
              type='button'
              isSelected={false}
              selectionStyle='light'
              elmId={`${elmId}-chip-${group.chip}`}
            />
          ) : (
            <button
              key={group.key}
              className={cx('dashed-chip')}
              onClick={onEditCriteria}
              data-elm-id={`${elmId}-add-${group.key}`}
            >
              <Icon name={group.addIconName} iconStyle='solid' size='small' />
              <span>{group.addLabel}</span>
            </button>
          )
        )}
      </div>
    </UtilityCard>
  )
}

export default BuyingCriteriaSummaryCard
